import React, { useMemo } from "react";
import { Sparkles, FileText, Lightbulb, ChevronDown, ChevronUp, Loader2, Paperclip, Upload, Trash2, LayoutTemplate } from "lucide-react";

// Side panels: previous records, AI suggestions, templates, attachments.

export function PreviousRecordsPanel({ granted, available, onRequest }) {
  const requestable = useMemo(() => available.filter((a) => !a.granted), [available]);
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="previous-records-panel">
      <p className="text-xs font-extrabold text-[#0A2540]">Client&apos;s Previous Consultation Records</p>
      <p className="text-[11px] text-slate-500 mb-2">Request access to a prior record — the client controls sharing.</p>
      {granted.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">Shared with you</p>
          <ul className="space-y-1">
            {granted.map((g) => (
              <li key={g.id} className="text-xs bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
                <p className="font-bold text-[#0A2540]">{g.matter_title}</p>
                <p className="text-slate-600 line-clamp-2">{g.legal_opinion || g.client_issue}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ul className="space-y-1">
        {requestable.map((a) => (
          <li key={a.lcr_id} className="text-xs flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
            <FileText size={14} className="text-slate-500"/>
            <span className="flex-1"><b>{a.matter_title}</b> <span className="text-slate-400">· {a.date}</span></span>
            <button
              onClick={() => onRequest(a)}
              className="px-2 py-1 text-[11px] font-bold rounded-full border border-slate-200 hover:border-[#0A2540]"
              data-testid={`request-access-${a.lcr_id}`}
            >
              {a.request_state === "pending" ? "Request pending"
                : a.request_state === "denied" ? "Denied"
                : "Request access"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SuggestionsPanel({ open, onToggle, items, onLoad, loading }) {
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="ai-suggestions-panel">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-slate-50"
        data-testid="ai-suggestions-toggle"
      >
        <Lightbulb size={16} className="text-[#D4AF37]"/>
        <div className="flex-1">
          <p className="text-xs font-extrabold text-[#0A2540]">AI suggestions</p>
          <p className="text-[11px] text-slate-500">Things to consider before publishing</p>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400"/> : <ChevronDown size={16} className="text-slate-400"/>}
      </button>
      {open && (
        <div className="px-4 pb-4">
          {items.length === 0 ? (
            <div className="text-[12px] text-slate-500 flex items-center gap-2">
              <button
                onClick={onLoad} disabled={loading}
                className="px-3 py-1.5 text-[11px] font-bold rounded-full border border-slate-200 hover:border-[#0A2540] disabled:opacity-60 inline-flex items-center gap-1"
                data-testid="ai-suggestions-load-btn"
              >
                {loading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
                {loading ? "Thinking…" : "Suggest what I might have missed"}
              </button>
            </div>
          ) : (
            <>
              <ul className="space-y-1.5 mt-1">
                {items.map((s, i) => (
                  <li key={`${s}-${i}`} className="text-xs bg-[#FBF4DF]/50 border border-[#D4AF37]/30 rounded-xl px-3 py-2 text-slate-700" data-testid={`ai-suggestion-${i}`}>
                    <span className="text-[#D4AF37] font-bold mr-1">•</span>{s}
                  </li>
                ))}
              </ul>
              <button
                onClick={onLoad} disabled={loading}
                className="mt-2 text-[11px] font-bold text-slate-500 hover:text-[#0A2540] inline-flex items-center gap-1"
                data-testid="ai-suggestions-refresh"
              >
                {loading ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                {loading ? "Refreshing…" : "Refresh suggestions"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function TemplateBar({ templates, onApply, onDelete }) {
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="lcr-template-bar">
      <p className="text-xs font-extrabold text-[#0A2540] flex items-center gap-1.5">
        <LayoutTemplate size={14} className="text-[#D4AF37]"/> Your templates
      </p>
      <p className="text-[11px] text-slate-500 mb-2">Private to you — click to pre-fill this record.</p>
      <div className="flex flex-wrap gap-2">
        {templates.map((t) => (
          <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 pl-3 pr-1.5 py-1">
            <button
              onClick={() => onApply(t)}
              className="text-[11px] font-bold text-slate-700 hover:text-[#0A2540]"
              data-testid={`apply-template-${t.id}`}
            >
              {t.name}
            </button>
            <button
              onClick={() => { if (window.confirm(`Delete template "${t.name}"?`)) onDelete(t.id); }}
              className="text-slate-400 hover:text-rose-500 p-0.5"
              data-testid={`delete-template-${t.id}`}
              title="Delete template"
            >
              <Trash2 size={11}/>
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

export function AttachmentsBlock({ attachments, uploading, onPick, onRemove, isPublished }) {
  const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
  return (
    <div data-testid="lcr-attachments">
      {(attachments || []).length > 0 && (
        <ul className="space-y-1 mb-2">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded-xl px-3 py-2" data-testid={`attachment-${a.id}`}>
              <Paperclip size={14} className="text-slate-500 shrink-0"/>
              <span className="flex-1 truncate font-medium text-slate-700">{a.name}</span>
              <span className="text-[11px] text-slate-400 shrink-0">{a.size ? fmtSize(a.size) : ""}</span>
              {!isPublished && (
                <button
                  className="text-[11px] text-rose-500 hover:underline shrink-0"
                  onClick={() => onRemove(a.id)}
                  data-testid={`remove-attachment-${a.id}`}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <button
        onClick={onPick}
        disabled={uploading}
        className="inline-flex items-center gap-2 px-4 h-10 text-xs font-bold rounded-xl border border-dashed border-slate-300 text-slate-600 hover:border-[#0A2540] hover:text-[#0A2540] disabled:opacity-60"
        data-testid="lcr-upload-attachment-btn"
      >
        {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
        {uploading ? "Uploading…" : "Upload document"}
      </button>
      <p className="mt-1.5 text-[10px] text-slate-400">PDF, JPG, PNG, WEBP, DOC, DOCX — max 10MB per file.</p>
    </div>
  );
}
